/**
 * Public legal pages (ALP-77): /privacy, /terms, /data-deletion. Meta requires a public privacy policy URL for live apps.
 * Section bodies: one paragraph per line; lines starting with "• " render as a bulleted list.
 */
export const legal = {
  nav: { privacy: "Privacy", terms: "Terms", deletion: "Data deletion", backHome: "Back to Malfak", signIn: "Sign in" },
  updated: "Last updated {{date}}",
  contactLine: "Questions about privacy or your data? Write to us:",
  privacy: {
    title: "Privacy policy",
    lead: "Malfak is a social-media management service for businesses. This policy explains what data we access when you connect an Instagram account, why, how long we keep it, and how to delete it.",
    sections: {
      who: {
        title: "Who we are",
        body: "Malfak (“we”) provides a shared inbox, publishing, automation and analytics for business and creator accounts on Instagram and, later, other channels.\nThe business that connects an account to Malfak (“the organization”) decides how its data is used; we process it on the organization's behalf.",
      },
      data: {
        title: "Instagram data we access",
        body: "When an organization owner or admin connects an Instagram professional account through Instagram's official login, and only with the permissions they approve:\n• Profile: account id, username, name, profile picture, follower count and account type.\n• Content: posts, reels, carousels and stories with captions, media links, likes and comment counts.\n• Comments on the account's posts, including replies and the public username of each commenter.\n• Direct messages with the account, within the limits Instagram allows (recent messages of each conversation).\n• Insights: reach, views, engagement and audience statistics of the account and its posts.\nWe never receive Instagram passwords, and we don't access personal Instagram accounts.",
      },
      why: {
        title: "Why we use it",
        body: "• To show comments and messages in one inbox so the team can answer them.\n• To run the automations the organization configures (for example hide spam or reply to price questions).\n• To publish the content the organization schedules.\n• To build dashboards and reports from insights.\n• To suggest replies and labels with AI when the organization turns AI on.\nWe don't sell data, we don't use it for advertising, and we don't build profiles of people across organizations.",
      },
      ai: {
        title: "AI processing",
        body: "When AI is on, comment and message text is sent through our own AI gateway to the model provider configured for the platform, only to produce the requested label or draft. Providers are contractually barred from training on it. History imported when you connect an account never triggers automations or automatic replies.",
      },
      storage: {
        title: "Storage and security",
        body: "• Instagram access tokens are encrypted at rest with AES-256-GCM and are never shown in the app, URLs or logs.\n• Data is stored in our cloud database in the region we operate, protected by access controls, encrypted transport (HTTPS) and audit logs of sensitive actions.\n• Access within an organization follows its roles (owner, admin, editor, viewer).",
      },
      retention: {
        title: "How long we keep it",
        body: "• By default an organization keeps its imported comments, messages and posts until it deletes them, disconnects and erases the account, or closes the organization.\n• Organization admins can set a retention period (for example 90 days) after which older comments and messages are purged automatically.\n• When an account is disconnected, its access token is deleted immediately.",
      },
      deletion: {
        title: "Deleting your data",
        body: "• Remove Malfak in Instagram → Settings → Apps and websites. Instagram notifies us, and we disconnect the account and delete its access token.\n• Ask for deletion from Instagram's “Apps and websites” screen, or email us. We erase the account's posts, comments, messages and related contacts and give you a confirmation code to follow the status at {{url}}.\n• Inside Malfak, admins can export or erase everything stored about a single person (commenter or contact).",
      },
      sharing: {
        title: "Sharing",
        body: "We share data only with the infrastructure providers that run Malfak for us (hosting, database, AI gateway) under data-processing terms, or when the law requires it. We never sell personal data.",
      },
      rights: {
        title: "Your rights",
        body: "Depending on where you live you can ask to access, correct, export or delete your data, or object to its processing. Commenters and customers who wrote to a business can contact that business or us directly.",
      },
      contact: {
        title: "Contact",
        body: "Privacy questions and requests: {{email}}. We answer within 30 days.",
      },
    },
  },
  terms: {
    title: "Terms of service",
    lead: "These terms govern the use of Malfak by businesses and their teams. By creating an organization or connecting an account you agree to them.",
    sections: {
      service: {
        title: "The service",
        body: "Malfak helps teams manage social-media accounts: a shared inbox for comments and messages, publishing, automations, AI-assisted replies and analytics. Features may change as channels change their APIs.",
      },
      accounts: {
        title: "Your organization and accounts",
        body: "• You must be allowed to manage every account you connect, and connect only professional (business or creator) Instagram accounts.\n• You are responsible for your team members, their roles and what they publish or send through Malfak.\n• Keep your sign-in details secure and tell us about any unauthorized use.",
      },
      platforms: {
        title: "Platform rules",
        body: "Using Malfak with Instagram also means following Meta's terms and policies, including messaging windows and publishing limits. Malfak enforces these limits and may refuse actions that would break them.",
      },
      acceptable: {
        title: "Acceptable use",
        body: "Don't use Malfak to send spam, harass people, publish unlawful content, scrape data you are not entitled to, or try to break or overload the service.",
      },
      ai: {
        title: "AI features",
        body: "AI suggestions can be wrong. Rules you set to “automatic” act without review; use approval mode when you want a person to check each reply. You remain responsible for what is sent from your accounts.",
      },
      data: {
        title: "Data",
        body: "Your organization owns its content. We process it to provide the service as described in our privacy policy, and delete it as described there.",
      },
      availability: {
        title: "Availability and changes",
        body: "We work to keep Malfak available but can't guarantee it will be uninterrupted, especially when a channel's API is down. We may update these terms; we'll announce material changes in the app.",
      },
      liability: {
        title: "Liability",
        body: "To the extent the law allows, Malfak is provided “as is” and our liability is limited to the fees paid for the service in the previous twelve months.",
      },
      termination: {
        title: "Ending",
        body: "You can disconnect accounts or close your organization at any time. We may suspend organizations that break these terms.",
      },
      contact: {
        title: "Contact",
        body: "Questions about these terms: {{email}}.",
      },
    },
  },
  deletion: {
    title: "Data deletion",
    lead: "How to delete the Instagram data Malfak holds, and the status of a request.",
    statusTitle: "Request status",
    code: "Confirmation code",
    requested: "Requested",
    completed: "Completed",
    pending: "Not finished yet",
    status: {
      received: "Received",
      in_progress: "In progress",
      completed: "Completed",
      failed: "Needs attention",
    },
    statusHint: {
      received: "We received your request and will start shortly.",
      in_progress: "We're erasing the data now. This page updates when it's done.",
      completed: "Everything Malfak held for this Instagram account has been erased.",
      failed: "Something stopped the deletion. Our team was notified and will finish it; you can also email us.",
    },
    summaryTitle: "What was erased",
    counts: {
      accounts: "Connected accounts",
      posts: "Posts",
      comments: "Comments",
      conversations: "Conversations",
      messages: "Messages",
      contacts: "Contacts",
    },
    notFound: "We couldn't find a request with this code. Check the code from Instagram, or email us.",
    invalidCode: "That doesn't look like a confirmation code.",
    loadFailed: "We couldn't load the status right now. Please try again.",
    retry: "Try again",
    howTitle: "How to request deletion",
    how: "• In the Instagram app open Settings → Apps and websites, choose Malfak and remove it. Instagram tells us and we disconnect the account and delete its access token.\n• From the same screen you can ask Instagram to send us a data-deletion request. You'll get a confirmation code: open this page with it to follow the status.\n• Or email {{email}} from an address we can verify, with your Instagram username.",
    lookupLabel: "Check a confirmation code",
    lookup: "Check status",
  },
};
